import httpClient from "../httpClient";

const ENDPOINTS = {
  NOTIFICATIONS: "/notifications",
  UNREAD_COUNT: "/notifications/count",
  MARK_READ: (uuid: string) => `/notifications/${uuid}/read`,
  MARK_ALL_READ: "/notifications/read-all",
  DELETE: (uuid: string) => `/notifications/${uuid}`,
};

export interface Notification {
  uuid: string;
  title: string;
  message: string;
  notification_type: 'marketing' | 'system' | 'announcement' | 'instructor' | 'event' | 'course';
  icon: string;
  action_url?: string;
  is_read: boolean;
  created_at: string;
  metadata?: any;
}

export interface NotificationResponse {
  success: boolean;
  data: Notification[];
  pagination?: {
    limit: number;
    offset: number;
  };
  error?: {
    message: string;
  };
}

export interface UnreadCountResponse {
  success: boolean;
  data: {
    unread_count: number;
  };
  error?: {
    message: string;
  };
}

/**
 * Get user's notifications with pagination and filters
 */
export const getUserNotifications = async (
  limit: number = 20,
  offset: number = 0,
  type?: string,
  isRead?: boolean
): Promise<NotificationResponse> => {
  try {
    const params = new URLSearchParams();
    params.append("limit", limit.toString());
    params.append("offset", offset.toString());
    if (type) params.append("type", type);
    if (isRead !== undefined) params.append("isRead", isRead.toString());

    const response = await httpClient.get<NotificationResponse>(
      `${ENDPOINTS.NOTIFICATIONS}?${params.toString()}`
    );
    return response;
  } catch (error: any) {
    console.error('Error fetching notifications:', error);
    return {
      success: false,
      data: [],
      error: { message: error.message || 'Failed to fetch notifications' }
    };
  }
};

/**
 * Get unread notification count
 */
export const getUnreadCount = async (): Promise<UnreadCountResponse> => {
  try {
    const response = await httpClient.get<UnreadCountResponse>(ENDPOINTS.UNREAD_COUNT);
    return response;
  } catch (error: any) {
    console.error('Error fetching unread count:', error);
    return {
      success: false,
      data: { unread_count: 0 },
      error: { message: error.message || 'Failed to fetch unread count' }
    };
  }
};

/**
 * Mark a single notification as read
 */
export const markAsRead = async (uuid: string): Promise<{ success: boolean; error?: { message: string } }> => {
  try {
    const response = await httpClient.put(ENDPOINTS.MARK_READ(uuid), {});
    return response;
  } catch (error: any) {
    console.error('Error marking notification as read:', error);
    return {
      success: false,
      error: { message: error.message || 'Failed to mark as read' }
    };
  }
};

/**
 * Mark all notifications as read
 */
export const markAllAsRead = async (): Promise<{ success: boolean; error?: { message: string } }> => {
  try {
    const response = await httpClient.put(ENDPOINTS.MARK_ALL_READ, {});
    return response;
  } catch (error: any) {
    console.error('Error marking all as read:', error);
    return {
      success: false,
      error: { message: error.message || 'Failed to mark all as read' }
    };
  }
};

/**
 * Delete (soft delete) a notification
 */
export const deleteNotification = async (uuid: string): Promise<{ success: boolean; error?: { message: string } }> => {
  try {
    const response = await httpClient.delete(ENDPOINTS.DELETE(uuid));
    return response;
  } catch (error: any) {
    console.error('Error deleting notification:', error);
    return {
      success: false,
      error: { message: error.message || 'Failed to delete notification' }
    };
  }
};
